import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { MAX_CARGO_FILES, MAX_CARGO_FILE_SIZE_LABEL, MAX_CARGO_TOTAL_SIZE_LABEL } from '../../hooks/useCreateQuote';
import type { FormDataState } from '../../hooks/useCreateQuote';

interface AdditionalInfoSectionProps {
  formData: FormDataState;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onDismissRejectedFiles: () => void;
  hasRejectedFiles: boolean;
  files: File[];
  cargoImageErrors?: string[];
  inputClass: string;
  labelClass: string;
}

export const AdditionalInfoSection: React.FC<AdditionalInfoSectionProps> = ({
  formData,
  handleChange,
  handleFileChange,
  onRemoveFile,
  onDismissRejectedFiles,
  hasRejectedFiles,
  files,
  cargoImageErrors,
  inputClass,
  labelClass
}) => {
  const { t } = useTranslation();
  const sectionTitleClass = "text-lg leading-none tracking-tight mb-4 text-foreground";
  const limits = {
    maxFiles: MAX_CARGO_FILES,
    maxSize: MAX_CARGO_FILE_SIZE_LABEL,
    maxTotal: MAX_CARGO_TOTAL_SIZE_LABEL,
  };

  return (
    <div>
      <h4 className={sectionTitleClass}>{t('quote.form.sections.additional')}</h4>
      <div className="space-y-4">
        <div>
          <label className={labelClass}>{t('quote.form.fields.observations.label')}</label>
          <textarea
            name="observations"
            className={`${inputClass} h-24 resize-none`}
            value={formData.observations}
            onChange={handleChange}
            placeholder={t('quote.form.fields.observations.placeholder')}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="cargo-images">{t('quote.form.fields.images.label')}</label>
          <input
            id="cargo-images"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileChange}
            aria-describedby="cargo-images-limits"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground hover:file:cursor-pointer"
          />
          <p id="cargo-images-limits" className="text-xs text-muted-foreground mt-2">
            {t('quote.form.fields.images.limits', limits)}
          </p>
          {files.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground">
                {t('quote.form.fields.images.selected', { count: files.length })}
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {files.map((file, index) => (
                  <span
                    key={`${file.name}-${file.size}-${index}`}
                    className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                  >
                    {file.name}
                    <button
                      type="button"
                      onClick={() => onRemoveFile(index)}
                      aria-label={t('quote.form.fields.images.remove_file', { filename: file.name })}
                      className="rounded text-slate-500 hover:text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          {cargoImageErrors && cargoImageErrors.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm text-destructive" role="alert">
              {cargoImageErrors.map((error) => (
                <li key={error}>{t(error, limits)}</li>
              ))}
            </ul>
          )}
          {hasRejectedFiles && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('quote.form.fields.images.rejected_notice')}
              </p>
              <button
                type="button"
                onClick={onDismissRejectedFiles}
                className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {files.length > 0
                  ? t('quote.form.fields.images.continue_with_files')
                  : t('quote.form.fields.images.continue_without_files')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
